import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

const BACKEND = 'https://alphabioai-backend.onrender.com';

const T = {
  bg:        '#0c0c0e',
  surface:   '#141416',
  surfaceHi: '#1c1c1f',
  border:    '#2e2e32',
  borderHi:  '#48484f',
  text:      '#f0f0f2',
  textSub:   '#a8a8b3',
  textMute:  '#6e6e7a',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  blueMid:   'rgba(59,130,246,0.18)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
  teal:      '#2dd4bf',
};

const fmt$ = (n) =>
  n != null ? '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';

const fmtPct = (n) =>
  n != null ? `${n >= 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '—';

const riskColor = (r) => r === 'High' ? T.red : r === 'Medium' ? T.amber : T.green;

const extractKeyword = (companyName) => {
  const cleaned = (companyName || '')
    .replace(/\b(Inc|Corp|Ltd|LLC|Co|Company|Pharmaceuticals?|Pharma|Biosciences?|Therapeutics?|Sciences?|Biotechnology|Biotech|Holdings?|Group|AG|SA|NV|plc)\b\.?/gi, '')
    .replace(/[^a-zA-Z\s]/g, '').trim();
  return cleaned.split(/\s+/).filter(Boolean)[0] || '';
};

function SummaryCard({ label, value, color, loading }) {
  return (
    <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: '8px', padding: '14px 16px' }}>
      <div style={{ fontSize: '11px', color: T.textMute, fontWeight: '700', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: "'DM Mono', monospace" }}>{label}</div>
      <div style={{ fontSize: '18px', fontWeight: '700', color: color || T.text, fontFamily: "'DM Mono', monospace", letterSpacing: '-0.5px' }}>
        {loading ? <span style={{ color: T.textMute }}>—</span> : value}
      </div>
    </div>
  );
}

function ActivityPanel({ activity, ticker, plan }) {
  const { fda_drugs = [], fda_calendar = [], trials = [] } = activity || {};
  const hasAny = fda_drugs.length + fda_calendar.length + trials.length > 0;
  if (!hasAny) return <div style={{ padding: '12px 0', color: T.textSub, fontSize: '14px' }}>No matching FDA or trial data found for {ticker}.</div>;
  return (
    <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
      {fda_drugs.length > 0 && (
        <DarkPanel title={`FDA Drugs (${fda_drugs.length})`} accent={T.blue}>
          {fda_drugs.map((d, i) => (
            <DarkRow key={i} last={i === fda_drugs.length - 1}>
              <div style={{ fontWeight: '600', fontSize: '13px', color: T.text }}>{d.drug_name}</div>
              <div style={{ fontSize: '12px', color: T.textSub, marginTop: '2px' }}>Risk: <span style={{ color: riskColor(d.risk_level), fontWeight: '700' }}>{d.risk_level}</span> · {d.manufacturer}</div>
            </DarkRow>
          ))}
        </DarkPanel>
      )}
      {fda_calendar.length > 0 && (
        <DarkPanel title={`FDA Events (${fda_calendar.length})`} accent={T.purple}>
          {fda_calendar.map((e, i) => (
            <DarkRow key={i} last={i === fda_calendar.length - 1}>
              <div style={{ fontWeight: '600', fontSize: '13px', color: T.text }}>{e.drug_name}</div>
              <div style={{ fontSize: '12px', color: T.textSub, marginTop: '2px' }}>{e.event_type} · {e.pdufa_date ? new Date(e.pdufa_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD'}</div>
            </DarkRow>
          ))}
        </DarkPanel>
      )}
      {plan === 'pro' && trials.length > 0 && (
        <DarkPanel title={`Trials (${trials.length})`} accent={T.teal}>
          {trials.map((t, i) => (
            <DarkRow key={i} last={i === trials.length - 1}>
              <div style={{ fontWeight: '600', fontSize: '13px', color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
              <div style={{ fontSize: '12px', color: T.textSub, marginTop: '2px' }}>{t.phase} · {t.status}</div>
            </DarkRow>
          ))}
        </DarkPanel>
      )}
      {plan !== 'pro' && (
        <div style={{ backgroundColor: T.surface, borderRadius: '8px', border: `1px dashed ${T.purple}40`, padding: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ fontSize: '18px' }}>🔬</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: T.purple }}>Clinical Trials — Pro only</div>
            <div style={{ fontSize: '12px', color: T.textSub, marginTop: '2px' }}>Upgrade to $49/mo for {ticker}.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function DarkPanel({ title, accent, children }) {
  return (
    <div style={{ backgroundColor: T.bg, borderRadius: '8px', border: `1px solid ${T.border}`, borderTop: `2px solid ${accent}`, padding: '12px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: accent, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: "'DM Mono', monospace" }}>{title}</div>
      {children}
    </div>
  );
}

function DarkRow({ children, last }) {
  return <div style={{ paddingBottom: last ? 0 : '10px', marginBottom: last ? 0 : '10px', borderBottom: last ? 'none' : `1px solid ${T.border}` }}>{children}</div>;
}

export default function PortfolioTracker({ session, plan }) {
  const [holdings, setHoldings] = useState([]);
  const [priceData, setPriceData] = useState({});
  const [relatedActivity, setRelatedActivity] = useState({});
  const [loadingHoldings, setLoadingHoldings] = useState(true);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [expandedTicker, setExpandedTicker] = useState(null);
  const [form, setForm] = useState({ ticker: '', shares: '', purchase_price: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const isMobile = windowWidth < 768;

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchHoldings = useCallback(async () => {
    setLoadingHoldings(true);
    const { data, error } = await supabase.from('portfolios').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false });
    if (!error) setHoldings(data || []);
    setLoadingHoldings(false);
  }, [session]);

  const fetchPrices = useCallback(async (list) => {
    if (!list.length) return;
    setLoadingPrices(true);
    const map = {};
    await Promise.all(list.map(async (h) => {
      try {
        const res = await fetch(`${BACKEND}/api/stock-price/${h.ticker}`);
        const data = await res.json();
        if (!data.error) map[h.ticker] = data;
      } catch {}
    }));
    setPriceData(map);
    setLoadingPrices(false);
  }, []);

  const fetchActivity = useCallback(async (ticker, companyName) => {
    const keyword = extractKeyword(companyName);
    if (!keyword || keyword.length < 3) return;
    const [drugsRes, calendarRes, trialsRes] = await Promise.all([
      supabase.from('fda_drugs').select('drug_name, manufacturer, risk_level').ilike('manufacturer', `%${keyword}%`).limit(5),
      supabase.from('fda_calendar').select('drug_name, company, pdufa_date, event_type').ilike('company', `%${keyword}%`).order('pdufa_date', { ascending: true }).limit(5),
      plan === 'pro' ? supabase.from('clinical_trials').select('title, status, phase, condition, risk_level').ilike('company', `%${keyword}%`).limit(5) : Promise.resolve({ data: [] }),
    ]);
    setRelatedActivity(prev => ({ ...prev, [ticker]: { fda_drugs: drugsRes.data || [], fda_calendar: calendarRes.data || [], trials: trialsRes.data || [], keyword } }));
  }, [plan]);

  useEffect(() => { fetchHoldings(); }, [fetchHoldings]);
  useEffect(() => {
    if (holdings.length) {
      fetchPrices(holdings);
      holdings.forEach(h => { if (h.company_name) fetchActivity(h.ticker, h.company_name); });
    }
  }, [holdings, fetchPrices, fetchActivity]);

  const handleAdd = async () => {
    const { ticker, shares, purchase_price } = form;
    if (!ticker.trim() || !shares) { setAddError('Ticker and shares are required.'); return; }
    setAddLoading(true); setAddError('');
    let company_name = ticker.toUpperCase().trim();
    try {
      const res = await fetch(`${BACKEND}/api/stock-price/${company_name}`);
      const data = await res.json();
      if (!data.error && data.company_name) company_name = data.company_name;
    } catch {}
    const { error } = await supabase.from('portfolios').upsert({
      user_id: session.user.id, ticker: ticker.toUpperCase().trim(), company_name,
      shares: parseFloat(shares), purchase_price: purchase_price ? parseFloat(purchase_price) : null,
      purchase_date: new Date().toISOString().split('T')[0],
    }, { onConflict: 'user_id,ticker' });
    if (error) { setAddError(error.message); } else { setForm({ ticker: '', shares: '', purchase_price: '' }); await fetchHoldings(); }
    setAddLoading(false);
  };

  const handleRemove = async (id, ticker) => {
    await supabase.from('portfolios').delete().eq('id', id);
    setHoldings(prev => prev.filter(h => h.id !== id));
    setPriceData(prev => { const c = { ...prev }; delete c[ticker]; return c; });
    setRelatedActivity(prev => { const c = { ...prev }; delete c[ticker]; return c; });
    if (expandedTicker === ticker) setExpandedTicker(null);
  };

  const totalValue = holdings.reduce((sum, h) => sum + (priceData[h.ticker]?.current_price || 0) * h.shares, 0);
  const holdingsWithCost = holdings.filter(h => h.purchase_price);
  const totalCost = holdingsWithCost.reduce((sum, h) => sum + h.purchase_price * h.shares, 0);
  const totalValueForCost = holdingsWithCost.reduce((sum, h) => sum + (priceData[h.ticker]?.current_price || 0) * h.shares, 0);
  const totalGain = totalCost > 0 ? totalValueForCost - totalCost : null;
  const totalGainPct = totalCost > 0 ? (totalGain / totalCost) * 100 : null;
  const anyHasActivity = holdings.some(h => { const a = relatedActivity[h.ticker]; return a && (a.fda_drugs.length + a.fda_calendar.length + a.trials.length > 0); });

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: T.text, margin: '0 0 4px' }}>Portfolio Tracker</h2>
        <p style={{ color: T.textSub, fontSize: '14px', margin: 0 }}>Track your biotech holdings with live prices and related FDA activity.</p>
      </div>

      {/* Summary cards */}
      {holdings.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fit,minmax(160px,1fr))', gap: '8px', marginBottom: '20px' }}>
          <SummaryCard label="Portfolio Value" value={fmt$(totalValue)} loading={loadingPrices} />
          {totalCost > 0 && <>
            <SummaryCard label="Cost Basis" value={fmt$(totalCost)} />
            <SummaryCard label="Gain / Loss" value={`${fmt$(totalGain)} (${fmtPct(totalGainPct)})`} color={totalGain >= 0 ? T.green : T.red} loading={loadingPrices} />
          </>}
          <SummaryCard label="Holdings" value={`${holdings.length} stock${holdings.length !== 1 ? 's' : ''}`} />
        </div>
      )}

      {/* Add holding form */}
      <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: T.text, margin: '0 0 12px' }}>Add Holding</h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <DarkField label="Ticker">
            <input value={form.ticker} onChange={e => setForm(f => ({ ...f, ticker: e.target.value.toUpperCase() }))} placeholder="MRNA"
              style={{ ...darkInput, width: isMobile ? '80px' : '110px' }} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
          </DarkField>
          <DarkField label="Shares">
            <input type="number" min="0" value={form.shares} onChange={e => setForm(f => ({ ...f, shares: e.target.value }))} placeholder="100"
              style={{ ...darkInput, width: isMobile ? '80px' : '110px' }} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
          </DarkField>
          {!isMobile && (
            <DarkField label="Avg Price (optional)">
              <input type="number" min="0" value={form.purchase_price} onChange={e => setForm(f => ({ ...f, purchase_price: e.target.value }))} placeholder="85.00"
                style={{ ...darkInput, width: '140px' }} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            </DarkField>
          )}
          <button onClick={handleAdd} disabled={addLoading}
            style={{ backgroundColor: addLoading ? T.textMute : T.blue, color: 'white', border: 'none', borderRadius: '6px', padding: '0 16px', height: '36px', fontSize: '13px', fontWeight: '600', cursor: addLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
            {addLoading ? '...' : '+ Add'}
          </button>
        </div>
        {isMobile && (
          <div style={{ marginTop: '8px' }}>
            <DarkField label="Avg Price (optional)">
              <input type="number" min="0" value={form.purchase_price} onChange={e => setForm(f => ({ ...f, purchase_price: e.target.value }))} placeholder="85.00"
                style={{ ...darkInput, width: '140px' }} onKeyDown={e => e.key === 'Enter' && handleAdd()} />
            </DarkField>
          </div>
        )}
        {addError && <p style={{ color: T.red, fontSize: '13px', margin: '10px 0 0' }}>⚠ {addError}</p>}
      </div>

      {/* Holdings */}
      {loadingHoldings ? (
        <div style={{ textAlign: 'center', padding: '60px', color: T.textMute }}>
          <div style={{ fontSize: '14px', marginBottom: '12px' }}>Loading portfolio...</div>
          <div style={{ width: '80px', height: '2px', backgroundColor: T.border, borderRadius: '1px', overflow: 'hidden', margin: '0 auto' }}>
            <div style={{ height: '100%', width: '50%', backgroundColor: T.blue, animation: 'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ) : holdings.length === 0 ? (
        <DarkEmptyState />
      ) : isMobile ? (
        /* ── MOBILE: Card layout ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {holdings.map(h => {
            const p = priceData[h.ticker];
            const value = p ? p.current_price * h.shares : null;
            const gain = h.purchase_price && p ? (p.current_price - h.purchase_price) * h.shares : null;
            const gainPct = h.purchase_price && p ? ((p.current_price - h.purchase_price) / h.purchase_price) * 100 : null;
            const activity = relatedActivity[h.ticker];
            const hasActivity = activity && (activity.fda_drugs.length + activity.fda_calendar.length + activity.trials.length > 0);
            const isExpanded = expandedTicker === h.ticker;

            return (
              <React.Fragment key={h.id}>
                <div onClick={() => setExpandedTicker(isExpanded ? null : h.ticker)}
                  style={{ backgroundColor: isExpanded ? T.surfaceHi : T.surface, border: `1px solid ${T.border}`, borderRadius: '8px', padding: '14px 16px', cursor: 'pointer' }}>
                  {/* Top row: ticker + remove */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: '700', color: T.blue, fontSize: '16px', fontFamily: "'DM Mono', monospace" }}>{h.ticker}</span>
                      {hasActivity && <span style={{ fontSize: '10px', backgroundColor: T.blueDim, color: T.blue, borderRadius: '3px', padding: '1px 5px', fontWeight: '700' }}>FDA</span>}
                    </div>
                    <button onClick={e => { e.stopPropagation(); handleRemove(h.id, h.ticker); }}
                      style={{ background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: '14px', padding: '4px' }}
                      onMouseEnter={e => e.currentTarget.style.color = T.red}
                      onMouseLeave={e => e.currentTarget.style.color = T.textMute}>✕</button>
                  </div>
                  {/* Company */}
                  <div style={{ fontSize: '13px', color: T.textSub, marginBottom: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.company_name || h.ticker}</div>
                  {/* Price row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: T.textMute, marginBottom: '2px', fontFamily: "'DM Mono', monospace" }}>PRICE</div>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: T.text, fontFamily: "'DM Mono', monospace" }}>
                        {p ? fmt$(p.current_price) : loadingPrices ? '…' : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: T.textMute, marginBottom: '2px', fontFamily: "'DM Mono', monospace" }}>DAY</div>
                      <div style={{ fontSize: '15px', fontWeight: '600', fontFamily: "'DM Mono', monospace", color: p ? (p.day_change >= 0 ? T.green : T.red) : T.textMute }}>
                        {p ? fmtPct(p.day_change_pct) : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: T.textMute, marginBottom: '2px', fontFamily: "'DM Mono', monospace" }}>VALUE</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: T.text, fontFamily: "'DM Mono', monospace" }}>{value ? fmt$(value) : '—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: T.textMute, marginBottom: '2px', fontFamily: "'DM Mono', monospace" }}>GAIN</div>
                      <div style={{ fontSize: '14px', fontWeight: '600', fontFamily: "'DM Mono', monospace", color: gain != null ? (gain >= 0 ? T.green : T.red) : T.textMute }}>
                        {gain != null ? fmtPct(gainPct) : '—'}
                      </div>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ padding: '0 4px 4px' }}>
                    <ActivityPanel activity={activity} ticker={h.ticker} plan={plan} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        /* ── DESKTOP: Table layout ── */
        <div style={{ backgroundColor: T.surface, border: `1px solid ${T.border}`, borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 70px 100px 100px 110px 150px 40px', padding: '11px 16px', backgroundColor: T.surfaceHi, borderBottom: `1px solid ${T.border}` }}>
            {['Ticker', 'Company', 'Shares', 'Price', 'Day Chg', 'Value', 'Gain / Loss', ''].map(h => (
              <div key={h} style={{ fontSize: '11px', fontWeight: '700', color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace" }}>{h}</div>
            ))}
          </div>
          {holdings.map(h => {
            const p = priceData[h.ticker];
            const value = p ? p.current_price * h.shares : null;
            const gain = h.purchase_price && p ? (p.current_price - h.purchase_price) * h.shares : null;
            const gainPct = h.purchase_price && p ? ((p.current_price - h.purchase_price) / h.purchase_price) * 100 : null;
            const activity = relatedActivity[h.ticker];
            const hasActivity = activity && (activity.fda_drugs.length + activity.fda_calendar.length + activity.trials.length > 0);
            const isExpanded = expandedTicker === h.ticker;
            return (
              <React.Fragment key={h.id}>
                <div onClick={() => setExpandedTicker(isExpanded ? null : h.ticker)}
                  style={{ display: 'grid', gridTemplateColumns: '90px 1fr 70px 100px 100px 110px 150px 40px', padding: '13px 16px', borderBottom: `1px solid ${T.border}`, alignItems: 'center', cursor: 'pointer', backgroundColor: isExpanded ? T.surfaceHi : T.surface, transition: 'background 0.12s' }}
                  onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = T.surfaceHi; }}
                  onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = T.surface; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontWeight: '700', color: T.blue, fontSize: '14px', fontFamily: "'DM Mono', monospace" }}>{h.ticker}</span>
                    {hasActivity && <span style={{ fontSize: '10px', backgroundColor: T.blueDim, color: T.blue, borderRadius: '3px', padding: '1px 5px', fontWeight: '700' }}>FDA</span>}
                  </div>
                  <div style={{ fontSize: '13px', color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }}>{h.company_name || h.ticker}</div>
                  <div style={{ fontSize: '13px', color: T.textSub, fontFamily: "'DM Mono', monospace" }}>{h.shares}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: T.text, fontFamily: "'DM Mono', monospace" }}>{p ? fmt$(p.current_price) : loadingPrices ? <span style={{ color: T.textMute }}>…</span> : '—'}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', fontFamily: "'DM Mono', monospace" }}>{p ? <span style={{ color: p.day_change >= 0 ? T.green : T.red }}>{fmtPct(p.day_change_pct)}</span> : <span style={{ color: T.textMute }}>—</span>}</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: T.text, fontFamily: "'DM Mono', monospace" }}>{value ? fmt$(value) : <span style={{ color: T.textMute }}>—</span>}</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', fontFamily: "'DM Mono', monospace" }}>
                    {gain != null ? <span style={{ color: gain >= 0 ? T.green : T.red }}>{fmt$(gain)}<br /><span style={{ fontSize: '12px' }}>({fmtPct(gainPct)})</span></span>
                      : h.purchase_price ? <span style={{ color: T.textMute }}>—</span>
                      : <span style={{ fontSize: '12px', color: T.textMute }}>No basis</span>}
                  </div>
                  <button onClick={e => { e.stopPropagation(); handleRemove(h.id, h.ticker); }}
                    style={{ background: 'none', border: 'none', color: T.textMute, cursor: 'pointer', fontSize: '15px', padding: '4px', lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.color = T.red}
                    onMouseLeave={e => e.currentTarget.style.color = T.textMute}>✕</button>
                </div>
                {isExpanded && (
                  <div style={{ padding: '4px 16px 16px', backgroundColor: T.surfaceHi, borderBottom: `1px solid ${T.border}` }}>
                    <ActivityPanel activity={activity} ticker={h.ticker} plan={plan} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {anyHasActivity && (
            <div style={{ padding: '10px 16px', fontSize: '12px', color: T.textMute, borderTop: `1px solid ${T.border}` }}>
              Click any row to see related FDA decisions, approved drugs, and clinical trials
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
        input::placeholder { color: #6e6e7a; }
      `}</style>
    </div>
  );
}

function DarkField({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: '700', color: T.textMute, marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace" }}>{label}</div>
      {children}
    </div>
  );
}

function DarkEmptyState() {
  return (
    <div style={{ textAlign: 'center', padding: '50px 24px', backgroundColor: '#141416', borderRadius: '8px', border: '1px dashed #2e2e32' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px', color: '#6e6e7a' }}>📊</div>
      <div style={{ fontWeight: '600', fontSize: '15px', color: '#a8a8b3', marginBottom: '6px' }}>No holdings yet</div>
      <div style={{ fontSize: '13px', color: '#6e6e7a', maxWidth: '320px', margin: '0 auto', lineHeight: 1.6 }}>
        Add your first biotech stock above to track live prices and FDA activity.
      </div>
    </div>
  );
}

const darkInput = {
  padding: '8px 12px', border: '1px solid #2e2e32', borderRadius: '6px',
  fontSize: '13px', color: '#f0f0f2', backgroundColor: '#0c0c0e',
  outline: 'none', height: '36px', boxSizing: 'border-box', display: 'block', transition: 'border-color 0.15s',
};